import { createMemo, createSignal } from 'solid-js';
import {
  anchoredDashboardX,
  createDefaultDashboardLayout,
  migrateDashboardLayoutV6,
  migrateLegacyDashboardLayout,
  sanitizeDashboardLayout,
  updateDashboardModuleSelection,
} from '~/config/dashboard/layout';
import { DASHBOARD_LAYOUT_STORAGE_KEY, getDashboardModuleRegistry } from '~/config/dashboard/modules';
import { DASHBOARD_COLUMNS, DASHBOARD_MAX_PREFERRED_ROW } from '~/config/dashboard/types';
import { usePropertyManagerPermissions } from '~/contexts/permissions/PropertyManagerPermissionsContext';
import { createMagicDoorContext } from '~/contexts/utils';
import type {
  DashboardLayoutConfig,
  DashboardModuleKey,
  DashboardResolvedModule,
  DashboardWidgetPlacement,
} from '~/config/dashboard/types';

const legacyStorageKey = 'home-layout';
const previousStorageKey = 'dashboard-layout-v6';

const cloneLayout = (layout: DashboardLayoutConfig): DashboardLayoutConfig => ({
  placements: layout.placements.map((placement) => ({ ...placement })),
  disabledModuleKeys: [...layout.disabledModuleKeys],
});

const layoutsEqual = (a: DashboardLayoutConfig, b: DashboardLayoutConfig) => JSON.stringify(a) === JSON.stringify(b);

const parseStoredValue = (key: string): unknown => {
  const value = localStorage.getItem(key);
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const loadDashboardLayout = (): DashboardLayoutConfig => {
  const definitions = getDashboardModuleRegistry();
  const stored = sanitizeDashboardLayout(parseStoredValue(DASHBOARD_LAYOUT_STORAGE_KEY), definitions);
  if (stored) return stored;
  const previous = migrateDashboardLayoutV6(parseStoredValue(previousStorageKey), definitions);
  if (previous) return previous;
  const migrated = migrateLegacyDashboardLayout(parseStoredValue(legacyStorageKey), definitions);
  return migrated ?? createDefaultDashboardLayout(definitions);
};

export const [CustomLayoutProvider, useCustomLayout] = createMagicDoorContext('CustomLayout', () => {
  const initialLayout = loadDashboardLayout();
  const definitions = createMemo(getDashboardModuleRegistry);
  const [enable, setEnable] = createSignal(false);
  const [savedLayout, setSavedLayout] = createSignal<DashboardLayoutConfig>(initialLayout);
  const [draftLayout, setDraftLayout] = createSignal<DashboardLayoutConfig>(cloneLayout(initialLayout));
  const { hasPermission, permissionsReady } = usePropertyManagerPermissions();

  const activeLayout = createMemo(() => (enable() ? draftLayout() : savedLayout()));

  const resolvedModules = createMemo<DashboardResolvedModule[]>(() => {
    const layout = activeLayout();
    const disabled = new Set(layout.disabledModuleKeys);
    const placements = new Map(layout.placements.map((placement) => [placement.key, placement]));
    return definitions()
      .filter((definition) => !disabled.has(definition.key))
      .filter((definition) => {
        if (!permissionsReady() || !definition.permissions) return true;
        return hasPermission(definition.permissions.resource, definition.permissions.action);
      })
      .flatMap((definition) => {
        const placement = placements.get(definition.key);
        return placement ? [{ definition, placement }] : [];
      });
  });

  const startEditing = () => {
    setDraftLayout(cloneLayout(savedLayout()));
    setEnable(true);
  };

  const cancelEditing = () => {
    setDraftLayout(cloneLayout(savedLayout()));
    setEnable(false);
  };

  const saveEditing = () => {
    const sanitized = sanitizeDashboardLayout(draftLayout(), definitions()) ?? createDefaultDashboardLayout(definitions());
    localStorage.setItem(DASHBOARD_LAYOUT_STORAGE_KEY, JSON.stringify(sanitized));
    setSavedLayout(cloneLayout(sanitized));
    setDraftLayout(cloneLayout(sanitized));
    setEnable(false);
  };

  const applyDraftChange = (change: (current: DashboardLayoutConfig) => DashboardLayoutConfig) => {
    const current = draftLayout();
    const next = change(current);
    if (next === current || layoutsEqual(current, next)) return;
    setDraftLayout(next);
  };

  const restoreDefault = () => {
    const defaultLayout = createDefaultDashboardLayout(definitions());
    applyDraftChange(() => defaultLayout);
  };

  const updateDraftPlacement = (
    key: DashboardModuleKey,
    update: Partial<Pick<DashboardWidgetPlacement, 'x' | 'width' | 'preferredRow' | 'fillEmptySpace'>>
  ) => {
    const definition = definitions().find((candidate) => candidate.key === key);
    applyDraftChange((current) => {
      const source = current.placements.find((placement) => placement.key === key);
      if (!source || !definition) return current;
      const width = Math.min(Math.max(Math.round(update.width ?? source.width), definition.minWidth), definition.maxWidth);
      const requestedX = update.x ?? (update.width === undefined ? source.x : anchoredDashboardX(source, width));
      const nextPlacement: DashboardWidgetPlacement = {
        ...source,
        ...update,
        width,
        x: Math.min(Math.max(Math.round(requestedX), 0), DASHBOARD_COLUMNS - width),
        preferredRow: Math.min(Math.max(0, Math.round(update.preferredRow ?? source.preferredRow)), DASHBOARD_MAX_PREFERRED_ROW),
      };
      const placements = current.placements.map((placement) => (placement.key === key ? nextPlacement : placement));
      return { ...current, placements };
    });
  };

  const updateDraftPlacements = (updates: DashboardWidgetPlacement[]) => {
    const updatesByKey = new Map(updates.map((placement) => [placement.key, placement]));
    const definitionsByKey = new Map(definitions().map((definition) => [definition.key, definition]));
    applyDraftChange((current) => {
      const placements = current.placements.map((source) => {
        const update = updatesByKey.get(source.key);
        const definition = definitionsByKey.get(source.key);
        if (!update || !definition || update.sectionId !== source.sectionId) return source;
        const width = Math.min(Math.max(Math.round(update.width), definition.minWidth), definition.maxWidth);
        return {
          ...source,
          x: Math.min(Math.max(Math.round(update.x), 0), DASHBOARD_COLUMNS - width),
          width,
          preferredRow: Math.min(Math.max(0, Math.round(update.preferredRow)), DASHBOARD_MAX_PREFERRED_ROW),
          layoutOrder: Math.max(0, Math.round(update.layoutOrder)),
        };
      });
      return { ...current, placements };
    });
  };

  const setModuleEnabled = (key: DashboardModuleKey, active: boolean) => {
    applyDraftChange((current) => {
      const disabled = new Set(current.disabledModuleKeys);
      if (active) disabled.delete(key);
      else disabled.add(key);
      return { ...current, disabledModuleKeys: [...disabled] };
    });
  };

  const setEnabledModuleKeys = (keys: DashboardModuleKey[], scopeKeys: DashboardModuleKey[]) => {
    const definitionKeys = definitions().map((definition) => definition.key);
    applyDraftChange((current) => updateDashboardModuleSelection(current, definitionKeys, keys, scopeKeys));
  };

  return {
    enable,
    draftLayout,
    resolvedModules,
    definitions,
    startEditing,
    cancelEditing,
    saveEditing,
    restoreDefault,
    updateDraftPlacement,
    updateDraftPlacements,
    setModuleEnabled,
    setEnabledModuleKeys,
  };
});
